import {
  ActionError,
  AttentionBlock,
  Button,
  PageHeader,
  PageTitle,
  QAPair,
  ReadingColumn,
  Skeleton,
  StepProgress,
  Surface,
  formatDate,
  plural,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import {
  useClinicProfile,
  useSaveClinicProfile,
  type ClinicProfile,
  type ClinicProfileInput,
} from '@/features/clinic-profile/api'
import {
  AddressBlock,
  AutoSignatureDialog,
  ClinicSignatureBlock,
  ContactBlock,
  DocumentField,
  IdentificationBlock,
  LegalRepresentativeBlock,
  WitnessesBlock,
} from '@/features/clinic-profile/blocks'
import { DOC_TYPE_LABELS, formatDocNumber } from '@/features/clinic-profile/doc-number'
import {
  ADDRESS_KEYS,
  CONTACT_KEYS,
  DOC_NUMBER_KEYS,
  IDENTIFICATION_KEYS,
  REPRESENTATIVE_KEYS,
  WITNESS_KEYS,
  clinicFieldKey,
  clinicFormFrom,
  companyInput,
  documentInput,
  hasNoWitness,
  placeFieldErrors,
  representativeInput,
  validateClinicForm,
  witnessesInput,
  type ClinicFormState,
} from '@/features/clinic-profile/form'
import { SIGNATURE_MODE_LABELS, addressLine } from '@/features/clinic-profile/labels'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import type { PaymentTerms } from '@/features/procedures/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  STEP_LABELS,
  useCompleteOnboarding,
  useConfirmOnboardingStep,
  useOnboarding,
  useSkipOnboarding,
  type Onboarding,
  type OnboardingStepKey,
} from './api'
import { OnboardingProcedures } from './onboarding-procedures'
import { ONBOARDING_STEPS } from './steps'

/**
 * Implantação da clínica (docs/areas/administracao.md, "Implantação da clínica"; RN-ADM-80 a 90).
 * Arquétipo: fluxo em etapas, da gestora ou de root com a clínica ativa. Abre na primeira etapa que
 * falta (ou na etapa pedida em `?step=`, o link de cada etapa no dashboard); cada etapa salva ao
 * avançar, e a pessoa pode voltar a uma etapa feita. Empresa e Testemunhas se confirmam;
 * Procedimentos é conferida sozinha pela API (RN-ADM-83). "Deixar para depois" adia (RN-ADM-90).
 */

const STEP_COPY: Record<OnboardingStepKey, { title: string; subtitle: string }> = {
  clinic_profile: {
    title: 'Confira os dados da empresa',
    subtitle: 'Estes dados entram em todo documento. Confira como se fosse o contrato.',
  },
  witnesses: {
    title: 'Testemunhas e assinatura da clínica',
    subtitle: 'Quem assina junto nos contratos e quem assina pela clínica.',
  },
  procedures: {
    title: 'Procedimentos da clínica',
    subtitle:
      'O que a clínica vende, com o nome e o valor dela. Cada um se liga a procedimentos da lista da Westhetics, que trazem os termos que o paciente assina.',
  },
  review: { title: 'Revisão da implantação', subtitle: 'O que a clínica passa a emitir.' },
}

const PROFILE_STEP_KEYS = [
  ...IDENTIFICATION_KEYS,
  ...ADDRESS_KEYS,
  ...CONTACT_KEYS,
  ...REPRESENTATIVE_KEYS,
]

/** A clínica de cadastro próprio preenche a empresa do zero: o título diz isso (RN-ADM-89). */
const SELF_SERVICE_PROFILE_COPY = {
  title: 'Complete os dados da empresa',
  subtitle: 'Estes dados entram em todo documento. Preencha como se fosse o contrato.',
}

const isStepKey = (value: string | null): value is OnboardingStepKey =>
  ONBOARDING_STEPS.includes(value as OnboardingStepKey)

export function OnboardingPage() {
  const onboarding = useOnboarding()
  const profile = useClinicProfile()

  let content: ReactNode
  if (onboarding.isError) {
    content = <LoadFailure error={onboarding.error} onRetry={() => void onboarding.refetch()} />
  } else if (!onboarding.data) {
    content = <StepSkeleton />
  } else if (onboarding.data.isCompleted) {
    content = <Completed onboarding={onboarding.data} />
  } else if (profile.isError) {
    content = <LoadFailure error={profile.error} onRetry={() => void profile.refetch()} />
  } else if (!profile.data) {
    content = <StepSkeleton />
  } else {
    content = (
      // O formulário nasce do cadastro carregado; outra clínica, outro fluxo.
      <Flow
        key={profile.data.id}
        onboarding={onboarding.data}
        profile={profile.data}
        onRefresh={() => void onboarding.refetch()}
      />
    )
  }

  return (
    <>
      <PageHeader crumbs={[{ label: 'Administração' }, { label: 'Implantação' }]} />
      <Page>{content}</Page>
    </>
  )
}

function LoadFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ReadingColumn>
      <PageTitle title="Implantação da clínica" />
      <LoadError
        className="mt-6"
        title="Não conseguimos abrir a implantação"
        error={error}
        onRetry={onRetry}
      />
    </ReadingColumn>
  )
}

/** O esqueleto de uma etapa: o progresso, o título e os blocos. */
function StepSkeleton() {
  return (
    <ReadingColumn>
      <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
        <Skeleton className="h-[30px] w-full" />
        <Skeleton className="mt-2 h-[26px] w-[280px] max-w-full" />
        <Skeleton className="h-[13px] w-[360px] max-w-full" />
        <Skeleton className="mt-2 h-[200px] w-full" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    </ReadingColumn>
  )
}

function Flow({
  onboarding,
  profile,
  onRefresh,
}: {
  onboarding: Onboarding
  profile: ClinicProfile
  onRefresh: () => void
}) {
  // A etapa que a pessoa escolheu (ou a pedida no endereço, pelo dashboard); sem escolha, a
  // primeira que falta (RN-ADM-81).
  const [searchParams] = useSearchParams()
  const requested = searchParams.get('step')
  // A etapa fica fixa ao abrir: o que a API recalcula (RN-ADM-83) não tira a pessoa de onde está.
  // Sem isso, cadastrar o primeiro procedimento pulava sozinho para a Revisão.
  const [selected, setSelected] = useState<OnboardingStepKey>(() =>
    isStepKey(requested) ? requested : (onboarding.currentStep ?? 'review'),
  )
  const step = selected
  const index = ONBOARDING_STEPS.indexOf(step)
  const navigate = useNavigate()

  const form = useFormState<ClinicFormState>(() => clinicFormFrom(profile), validateClinicForm)
  const save = useSaveClinicProfile()
  const confirm = useConfirmOnboardingStep()
  const complete = useCompleteOnboarding()
  const skip = useSkipOnboarding()
  const [failure, setFailure] = useState<unknown>(null)
  const [confirmingAutomatic, setConfirmingAutomatic] = useState(false)
  // Na etapa Procedimentos, o formulário de um procedimento novo aberto (até Concluir, sempre dá
  // para adicionar mais um) e a condição de pagamento do último, que o próximo reaproveita.
  const [isAddingProcedure, setAddingProcedure] = useState(false)
  const [lastPaymentTerms, setLastPaymentTerms] = useState<PaymentTerms | null>(null)
  const topRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const isSaving = save.isPending || confirm.isPending
  const isSelfService = onboarding.source === 'self_service'
  const profileKeys = profile.isDocNumberEditable
    ? [...DOC_NUMBER_KEYS, ...PROFILE_STEP_KEYS]
    : PROFILE_STEP_KEYS

  function goTo(next: OnboardingStepKey) {
    setFailure(null)
    setSelected(next)
    const top = topRef.current
    if (top && typeof top.scrollIntoView === 'function') top.scrollIntoView({ block: 'start' })
  }

  /** Da revisão (ou de qualquer etapa): volta à etapa Procedimentos com o formulário aberto. */
  function addProcedure() {
    setAddingProcedure(true)
    goTo('procedures')
  }

  /** Empresa e Testemunhas: salva o cadastro, confirma a etapa e avança (RN-ADM-82). */
  async function saveAndConfirm(
    key: 'clinic_profile' | 'witnesses',
    input: ClinicProfileInput,
    next: OnboardingStepKey,
  ) {
    const values = form.values
    setFailure(null)
    try {
      await save.mutateAsync(input)
      await confirm.mutateAsync(key)
      form.markSaved()
      goTo(next)
    } catch (error) {
      setFailure(error)
      form.refuse(placeFieldErrors(error, (path) => clinicFieldKey(path, values)).fields)
      focusFirstInvalid(formRef.current)
    } finally {
      setConfirmingAutomatic(false)
    }
  }

  const saveWitnesses = () =>
    void saveAndConfirm('witnesses', witnessesInput(form.values), 'procedures')

  function submit(event: FormEvent) {
    event.preventDefault()
    const values = form.values
    if (step === 'clinic_profile') {
      if (!form.check(profileKeys)) return focusFirstInvalid(formRef.current)
      void saveAndConfirm(
        'clinic_profile',
        {
          ...(profile.isDocNumberEditable ? documentInput(values) : {}),
          ...companyInput(values),
          ...representativeInput(values),
        },
        'witnesses',
      )
    } else if (step === 'witnesses') {
      if (!form.check(WITNESS_KEYS)) return focusFirstInvalid(formRef.current)
      // Ativar a assinatura automática pede confirmação antes de salvar (RN-ADM-07).
      const isActivating =
        values.clinicSignatureMode === 'automatic' && profile.clinicSignatureMode === 'manual'
      if (isActivating) setConfirmingAutomatic(true)
      else saveWitnesses()
    } else {
      setFailure(null)
      complete.mutate(undefined, {
        onSuccess: () => toast('Implantação concluída — a clínica já pode emitir documentos'),
        onError: (error) => {
          setFailure(error)
          // O que falta pode ter mudado (RN-ADM-83): a tela busca o estado de novo, mas a pessoa
          // continua na revisão, com a causa na frente e o caminho até a etapa que falta.
          setSelected('review')
          onRefresh()
        },
      })
    }
  }

  const placed = failure
    ? placeFieldErrors(failure, (path) => clinicFieldKey(path, form.values))
    : null
  const showFailure =
    placed !== null && (Object.keys(placed.fields).length === 0 || placed.unplaced.length > 0)

  const { summary } = onboarding
  const isProceduresDone =
    onboarding.steps.find((item) => item.key === 'procedures')?.status === 'done'
  const pendingLinks = summary.pendingCatalogLinkCount

  /** Adiar (RN-ADM-90): a porta de entrada deixa de trazer para cá; o dashboard lembra. */
  function skipForNow() {
    setFailure(null)
    skip.mutate(undefined, {
      onSuccess: () => navigate(routes.dashboard),
      onError: (error) => setFailure(error),
    })
  }

  const back =
    index === 0 ? (
      <Button
        className="max-desk:min-h-touch"
        variant="outline"
        loading={skip.isPending}
        loadingLabel="Guardando…"
        onClick={skipForNow}
      >
        Deixar para depois
      </Button>
    ) : (
      <Button
        className="max-desk:min-h-touch"
        variant="outline"
        onClick={() => goTo(ONBOARDING_STEPS[index - 1]!)}
      >
        Voltar
      </Button>
    )

  const title = (
    <>
      <StepProgress current={index + 1} total={ONBOARDING_STEPS.length} label={STEP_LABELS[step]} />
      <PageTitle
        className="mt-6"
        title={
          step === 'clinic_profile' && isSelfService
            ? SELF_SERVICE_PROFILE_COPY.title
            : STEP_COPY[step].title
        }
        subtitle={
          step === 'clinic_profile' && isSelfService
            ? SELF_SERVICE_PROFILE_COPY.subtitle
            : STEP_COPY[step].subtitle
        }
      />
    </>
  )

  const footer = (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <p className="font-body text-eyebrow text-label leading-[1.5] font-light tracking-normal">
        {index === 0
          ? 'Cada etapa é salva ao avançar. Deixar para depois guarda o que já foi feito: você continua pelo dashboard.'
          : 'Cada etapa é salva ao avançar. Você pode sair e voltar de onde parou.'}
      </p>
      <div className="flex flex-wrap gap-3">
        {back}
        {step === 'clinic_profile' ? (
          <Button
            className="max-desk:min-h-touch"
            type="submit"
            loading={isSaving}
            loadingLabel="Salvando a empresa…"
          >
            Confirmar e avançar
          </Button>
        ) : null}
        {step === 'witnesses' ? (
          <Button
            className="max-desk:min-h-touch"
            type="submit"
            loading={isSaving}
            loadingLabel="Salvando as testemunhas…"
          >
            {hasNoWitness(form.values) ? 'Seguir sem testemunhas' : 'Confirmar e avançar'}
          </Button>
        ) : null}
        {step === 'procedures' ? (
          <Button
            className="max-desk:min-h-touch"
            disabled={!isProceduresDone}
            onClick={() => goTo('review')}
          >
            {isProceduresDone
              ? 'Avançar'
              : pendingLinks === 1
                ? 'Falta 1 sem vínculo'
                : pendingLinks > 1
                  ? `Faltam ${pendingLinks} sem vínculo`
                  : 'Adicione um procedimento'}
          </Button>
        ) : null}
        {step === 'review' ? (
          <Button
            className="max-desk:min-h-touch"
            type="submit"
            loading={complete.isPending}
            loadingLabel="Concluindo a implantação…"
          >
            Concluir implantação
          </Button>
        ) : null}
      </div>
    </div>
  )

  // A etapa Procedimentos tem formulários próprios (o procedimento novo): fica fora do <form> das
  // outras etapas, que não podem ser aninhados.
  return (
    <div ref={topRef} className="scroll-mt-[80px]">
      <ReadingColumn wide={step === 'procedures'}>
        {title}
        {step === 'procedures' ? (
          <>
            <div className="mt-6 flex flex-col gap-4">
              <OnboardingProcedures
                summary={summary}
                isAdding={isAddingProcedure}
                onAddingChange={setAddingProcedure}
                paymentTerms={lastPaymentTerms}
                onAdded={(paymentTerms) => {
                  setLastPaymentTerms(paymentTerms)
                  onRefresh()
                }}
              />
            </div>
            {footer}
          </>
        ) : (
          <form ref={formRef} onSubmit={submit} noValidate>
            <div className="mt-6 flex flex-col gap-4">
              {showFailure ? (
                <ActionError
                  title={
                    step === 'review'
                      ? 'Não foi possível concluir a implantação'
                      : 'Não foi possível salvar esta etapa'
                  }
                  description={
                    placed.unplaced.length > 0
                      ? placed.unplaced.join(' ')
                      : step === 'review'
                        ? errorMessage(failure)
                        : `${errorMessage(failure)} O que você digitou continua na tela.`
                  }
                  actions={
                    step === 'review' &&
                    onboarding.currentStep &&
                    onboarding.currentStep !== 'review' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goTo(onboarding.currentStep ?? 'review')}
                      >
                        Ir para {STEP_LABELS[onboarding.currentStep]}
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}

              {step === 'clinic_profile' ? (
                <>
                  <IdentificationBlock
                    form={form}
                    note="Aparece no cabeçalho e na qualificação das partes."
                    document={<DocumentField form={form} profile={profile} />}
                  />
                  <AddressBlock form={form} />
                  <ContactBlock form={form} />
                  <LegalRepresentativeBlock form={form} />
                </>
              ) : null}

              {step === 'witnesses' ? (
                <>
                  <WitnessesBlock form={form} />
                  <ClinicSignatureBlock form={form} autoSignature={profile.autoSignature} />
                </>
              ) : null}

              {step === 'review' ? (
                <Review onboarding={onboarding} profile={profile} onAddProcedure={addProcedure} />
              ) : null}
            </div>
            {footer}
          </form>
        )}
      </ReadingColumn>

      <AutoSignatureDialog
        open={confirmingAutomatic}
        onOpenChange={setConfirmingAutomatic}
        loading={isSaving}
        onConfirm={saveWitnesses}
      />
    </div>
  )
}

/** O resumo em leitura do que a clínica passa a emitir (etapa 4). */
function Review({
  onboarding,
  profile,
  onAddProcedure,
}: {
  onboarding: Onboarding
  profile: ClinicProfile
  /** Até Concluir, sempre dá para adicionar mais um procedimento. */
  onAddProcedure: () => void
}) {
  const { summary } = onboarding
  const withoutConsentForm = summary.proceduresWithoutConsentFormCount
  return (
    <>
      <Surface className="py-2">
        <QAPair
          question="Empresa"
          answer={
            <>
              {profile.legalName}
              <div className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
                {DOC_TYPE_LABELS[profile.docType]}{' '}
                {formatDocNumber(profile.docNumber, profile.docType)} ·{' '}
                {addressLine(profile.address)}
              </div>
            </>
          }
        />
        <QAPair
          question="Testemunhas"
          answer={
            profile.witnesses.length === 0
              ? 'Sem testemunhas'
              : profile.witnesses.map((witness) => titleCase(witness.name)).join(' e ')
          }
        />
        <QAPair
          question="Assinatura da clínica"
          answer={SIGNATURE_MODE_LABELS[profile.clinicSignatureMode]}
        />
        <QAPair
          question="Procedimentos"
          answer={
            <>
              {plural(summary.procedureCount, 'procedimento')} ·{' '}
              {plural(summary.consentFormCount, 'termo')} de consentimento
              <div className="mt-1">
                <Button variant="text" className="text-[12px]" onClick={onAddProcedure}>
                  + Adicionar mais um procedimento
                </Button>
              </div>
            </>
          }
        />
        <QAPair
          question="Em todo atendimento"
          answer="Anamnese e contrato de prestação de serviços"
        />
      </Surface>
      {withoutConsentForm > 0 ? (
        <AttentionBlock
          title={
            withoutConsentForm === 1
              ? '1 procedimento ainda sem termo publicado pela Westhetics'
              : `${withoutConsentForm} procedimentos ainda sem termo publicado pela Westhetics`
          }
        >
          Isso não impede concluir. O termo volta a ser cobrado na hora de gerar os documentos de
          cada um.
        </AttentionBlock>
      ) : null}
    </>
  )
}

/** A tela final: o que aconteceu e para onde ir (RN-ADM-84). */
function Completed({ onboarding }: { onboarding: Onboarding }) {
  const { summary } = onboarding
  return (
    <ReadingColumn>
      <PageTitle
        title="Clínica pronta."
        subtitle="Você já pode cadastrar pacientes e gerar documentos."
      />
      <Surface className="mt-6">
        <div className="font-display text-lead text-ink leading-[1.3] font-normal">
          {completedLine(onboarding)}
        </div>
        <p className="font-body text-body text-secondary mt-2 font-light">
          {plural(summary.procedureCount, 'procedimento')} no catálogo ·{' '}
          {plural(summary.consentFormCount, 'termo')} de consentimento ·{' '}
          {summary.witnessCount === 0
            ? 'sem testemunhas'
            : plural(summary.witnessCount, 'testemunha')}
        </p>
      </Surface>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button className="max-desk:min-h-touch" asChild>
          <Link to={routes.patients}>Ir para Pacientes</Link>
        </Button>
        <Button className="max-desk:min-h-touch" variant="outline" asChild>
          <Link to={routes.admin.users}>Convidar a equipe</Link>
        </Button>
      </div>
      <p className="font-body text-secondary mt-6 text-[12px] leading-[1.8] font-light">
        Depois, complete o cadastro em{' '}
        <Button variant="text" className="text-[12px]" asChild>
          <Link to={routes.admin.clinicProfile}>Empresa</Link>
        </Button>{' '}
        e cadastre quem atende em{' '}
        <Button variant="text" className="text-[12px]" asChild>
          <Link to={routes.admin.practitioners}>Prestadores</Link>
        </Button>
        .
      </p>
    </ReadingColumn>
  )
}

/** "Implantação concluída em 03/10/2026 por Marina Alves Teixeira". */
function completedLine(onboarding: Onboarding) {
  const when = onboarding.completedAt ? ` em ${formatDate(new Date(onboarding.completedAt))}` : ''
  const who = onboarding.completedBy ? ` por ${titleCase(onboarding.completedBy.name)}` : ''
  return `Implantação concluída${when}${who}`
}
